import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useShop } from "@/context/ShopContext";
import { formatPrice, resolveImageUrl } from "@/lib/api";

export default function Cart() {
  const { cart, updateCart, removeFromCart } = useShop();

  if (cart.items.length === 0) {
    return (
      <div data-testid="cart-empty" className="mx-auto max-w-3xl px-4 py-24 text-center">
        <ShoppingBag className="mx-auto text-muted-soft" size={40} />
        <h1 className="mt-6 text-3xl font-extrabold tracking-tightest text-ink">Корзина пуста</h1>
        <p className="mt-3 text-sm text-muted">Добавьте товары из каталога, чтобы оформить заказ.</p>
        <Link to="/" className="mt-8 inline-block rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-ink-soft">
          Перейти в каталог
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="mx-auto max-w-7xl px-4 py-10 lg:px-8">
      <div className="mb-8">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-muted-soft">Оформление</div>
        <h1 className="mt-2 text-4xl font-extrabold tracking-tightest text-ink md:text-5xl">Корзина</h1>
      </div>

      <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <ul className="divide-y divide-line rounded-2xl border border-line bg-white">
            {cart.items.map((it) => (
              <li key={`${it.product_id}-${it.variation_id}`} data-testid={`cart-item-${it.product_slug}`} className="flex flex-col gap-4 p-5 sm:flex-row sm:items-center">
                <Link to={`/product/${it.product_slug}`} className="h-24 w-24 shrink-0 overflow-hidden rounded-xl bg-paper">
                  <img src={resolveImageUrl(it.image)} alt="" className="h-full w-full object-cover" />
                </Link>
                <div className="flex-1">
                  <Link to={`/product/${it.product_slug}`} className="font-semibold text-ink hover:underline">
                    {it.product_name}
                  </Link>
                  <div className="mt-1 flex flex-wrap gap-2 text-xs text-muted">
                    <span>Цвет: {it.variation.color}</span>
                    {(it.variation.memory || it.variation.storage) && <span>· Память: {it.variation.memory || it.variation.storage}</span>}
                    {it.variation.region && <span>· Связь: {it.variation.region}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center rounded-full border border-line bg-white">
                    <button
                      data-testid={`qty-dec-${it.product_slug}`}
                      onClick={() => updateCart(it.product_id, it.variation_id, it.quantity - 1)}
                      className="grid h-9 w-9 place-items-center text-ink transition-colors hover:bg-black/5"
                      aria-label="Уменьшить"
                    >
                      <Minus size={14} />
                    </button>
                    <span className="min-w-[36px] px-2 text-center text-sm font-semibold">{it.quantity}</span>
                    <button
                      data-testid={`qty-inc-${it.product_slug}`}
                      onClick={() => updateCart(it.product_id, it.variation_id, it.quantity + 1)}
                      className="grid h-9 w-9 place-items-center text-ink transition-colors hover:bg-black/5"
                      aria-label="Увеличить"
                    >
                      <Plus size={14} />
                    </button>
                  </div>
                  <div className="min-w-[110px] text-right font-bold tracking-tight text-ink">
                    {formatPrice(it.line_total)}
                  </div>
                  <button
                    data-testid={`remove-item-${it.product_slug}`}
                    onClick={() => removeFromCart(it.product_id, it.variation_id)}
                    className="grid h-9 w-9 place-items-center rounded-full text-muted transition-colors hover:bg-danger/10 hover:text-danger"
                    aria-label="Удалить"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-24 rounded-2xl border border-line bg-white p-6">
            <h2 className="text-lg font-bold tracking-tight text-ink">Итого</h2>
            <div className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between text-muted"><span>Товаров</span><span>{cart.count}</span></div>
              <div className="flex justify-between text-muted"><span>Доставка</span><span>Рассчитывается</span></div>
              <div className="mt-3 border-t border-line pt-3">
                <div className="flex items-baseline justify-between">
                  <span className="text-sm text-muted">К оплате</span>
                  <span className="text-2xl font-extrabold tracking-tightest text-ink">{formatPrice(cart.subtotal)}</span>
                </div>
              </div>
            </div>
            <Link
              to="/checkout"
              data-testid="checkout-cta"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-ink py-3 text-sm font-semibold text-white transition-colors hover:bg-ink-soft"
            >
              Оформить заказ
            </Link>
          </div>
        </aside>
      </div>
    </div>
  );
}
